// src/components/SmartWorkspaceInline.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Chip,
  Stack,
  TextField,
  IconButton,
  InputAdornment,
  Tooltip,
  Fade,
} from "@mui/material";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import DoneRoundedIcon from "@mui/icons-material/DoneRounded";
import ClearRoundedIcon from "@mui/icons-material/ClearRounded";
import HistoryRoundedIcon from "@mui/icons-material/HistoryRounded";
import {
  clearTabWorkspaceDatabase,
  setActiveWorkspaceDatabase,
} from "../utils/workspaceStorage";

// Aceita apenas minúsculas + números . _ -
const COMPANY_RE = /^[a-z0-9._-]{2,32}$/;

const toLower = (s) => String(s ?? "").trim().toLowerCase();

function loadRecent() {
  try {
    const raw = localStorage.getItem("recentCompanies");
    const arr = JSON.parse(raw);
    const list = Array.isArray(arr) ? arr : [];
    // normaliza tudo para minúsculo e remove vazios
    return list
      .map(toLower)
      .filter(Boolean)
      .filter((v, i, a) => a.indexOf(v) === i)
      .slice(0, 6);
  } catch {
    return [];
  }
}
function saveRecent(list) {
  try {
    const norm = list
      .map(toLower)
      .filter(Boolean)
      .filter((v, i, a) => a.indexOf(v) === i)
      .slice(0, 6);
    localStorage.setItem("recentCompanies", JSON.stringify(norm));
  } catch {}
}
function pushRecent(slug) {
  const v = toLower(slug);
  if (!v) return;
  const cur = loadRecent();
  const next = [v, ...cur.filter((x) => x !== v)].slice(0, 6);
  saveRecent(next);
}
function removeRecent(slug) {
  const next = loadRecent().filter((x) => x !== toLower(slug));
  saveRecent(next);
  return next;
}

const DEFAULT_INVALID_HELPER =
  "2–32 caracteres: minúsculas/números, '-' '_' '.' (ex.: acme)";

const WORKSPACE_DB_MIRROR_KEY = "xproWorkspaceDatabase";

export default function SmartWorkspaceInline({
  value, // string atual (controlado pelo parent)
  onSave, // (newValue) => void
  onEnterNext, // () => void
  name = "company",
  label = "Workspace",
  invalidHelperText,
  inputRef: externalInputRef,
  /** Quando true, campo vazio é válido e persiste "" (ex.: login master sem tenant). */
  allowEmpty = false,
}) {
  // sempre manter value minúsculo na UI
  const normalizedValue = toLower(value);
  const [mode, setMode] = useState(() => (normalizedValue ? "display" : "edit")); // display | edit
  const [draft, setDraft] = useState(normalizedValue || "");
  const [touched, setTouched] = useState(false);
  const [recent, setRecent] = useState(() => loadRecent());
  const inputRef = useRef(null);
  const ignoreBlurUntilRef = useRef(0);
  const rootRef = useRef(null);

  const assignInputRef = (el) => {
    inputRef.current = el;
    if (externalInputRef == null) return;
    if (typeof externalInputRef === "function") {
      externalInputRef(el);
    } else {
      externalInputRef.current = el;
    }
  };

  useEffect(() => {
    setDraft(toLower(value));
    if (!value) setMode("edit");
    setRecent(loadRecent());
  }, [value]);

  const isValid = useMemo(() => {
    const d = toLower(draft);
    if (allowEmpty && !d) return true;
    return COMPANY_RE.test(d);
  }, [draft, allowEmpty]);

  const commitEmptyWorkspace = () => {
    if (!allowEmpty) return false;
    clearTabWorkspaceDatabase();
    try {
      localStorage.removeItem("company");
      localStorage.removeItem(WORKSPACE_DB_MIRROR_KEY);
    } catch {
      /* ignore */
    }
    onSave?.("");
    setTouched(false);
    setMode("edit");
    return true;
  };

  const enterEdit = () => {
    setMode("edit");
    ignoreBlurUntilRef.current = Date.now() + 600;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        inputRef.current?.focus?.({ preventScroll: true });
      });
    });
  };

  const handleBlur = (e) => {
    if (Date.now() < ignoreBlurUntilRef.current) return;
    const related = e.relatedTarget;
    if (related && rootRef.current?.contains(related)) return;
    confirmEdit(null, { goNext: false });
  };

  const cancelEdit = () => {
    setDraft(normalizedValue || "");
    setTouched(false);
    setMode(normalizedValue ? "display" : "edit");
  };

  const confirmEdit = (nextValue, { goNext = true } = {}) => {
    const v = toLower(nextValue != null ? nextValue : draft);
    if (!v) {
      if (commitEmptyWorkspace()) {
        if (goNext) onEnterNext?.();
      } else {
        setTouched(true);
      }
      return;
    }
    if (!COMPANY_RE.test(v)) {
      setTouched(true);
      return;
    }
    if (v !== normalizedValue) {
      // Pin da aba + localStorage — o header Company prioriza sessionStorage.
      setActiveWorkspaceDatabase(v);
      pushRecent(v);
      setRecent(loadRecent());
      onSave?.(v);
    }
    setTouched(false);
    setMode("display");
    if (goNext) onEnterNext?.();
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      confirmEdit();
    } else if (e.key === "Escape") {
      e.preventDefault();
      cancelEdit();
    }
  };

  // Lista de recentes filtrada para não repetir o draft atual
  const recentToShow = recent.filter((slug) => slug && slug !== toLower(draft));

  if (mode === "display") {
    return (
      <Stack gap={0.75} ref={rootRef}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, width: "100%" }}>
          <Box
            onClick={enterEdit}
            onMouseDown={(e) => e.preventDefault()}
            sx={{
              flex: 1,
              borderRadius: 2,
              px: 1.25,
              py: 1,
              border: (t) => `1px solid ${t.palette.divider}`,
              bgcolor: (t) => t.palette.background.paper,
              cursor: "text",
              position: "relative",
              "&:hover": { boxShadow: (t) => t.shadows[1] },
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, minHeight: 28 }}>
              <Box sx={{ fontSize: 14, color: "text.secondary", mr: 0.5, whiteSpace: "nowrap" }}>
                {label}:
              </Box>
              <Box sx={{ fontWeight: 600, fontSize: 15, letterSpacing: 0.2 }} title={`${normalizedValue}.xpro.me`}>
                {normalizedValue}
              </Box>

              <Fade in timeout={150}>
                <IconButton
                  size="small"
                  sx={{ ml: "auto" }}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    enterEdit();
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    enterEdit();
                  }}
                >
                  <EditRoundedIcon fontSize="small" />
                </IconButton>
              </Fade>
            </Box>
          </Box>
        </Box>

        {recentToShow.length > 0 && (
          <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
            <HistoryRoundedIcon fontSize="small" sx={{ color: "text.secondary" }} />
            {recentToShow.map((slug) => (
              <Chip
                key={slug}
                label={slug}
                size="small"
                variant="outlined"
                onClick={() => {
                  const v = toLower(slug);
                  setDraft(v);
                  setActiveWorkspaceDatabase(v);
                  pushRecent(v);
                  setRecent(loadRecent());
                  onSave?.(v);
                  onEnterNext?.();
                }}
                onDelete={() => {
                  const next = removeRecent(slug);
                  setRecent(next);
                }}
              />
            ))}
          </Stack>
        )}
      </Stack>
    );
  }

  // mode === "edit"
  return (
    <Stack gap={0.75} ref={rootRef}>
      <TextField
        inputRef={assignInputRef}
        name={name}
        label={label}
        placeholder="ex.: acme"
        value={draft}
        onChange={(e) => {
          // força minúsculo em tempo real
          const v = toLower(e.target.value);
          setDraft(v);
          if (!touched) setTouched(true);
          if (allowEmpty && !v) {
            commitEmptyWorkspace();
          }
        }}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        fullWidth
        autoFocus
        inputProps={{ autoComplete: "off" }}
        error={touched && !isValid}
        helperText={touched && !isValid ? invalidHelperText || DEFAULT_INVALID_HELPER : undefined}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end" sx={{ pr: 0.25 }}>
              {draft && (
                <Tooltip title="Limpar">
                  <IconButton
                    size="small"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => {
                      setDraft("");
                      if (allowEmpty) commitEmptyWorkspace();
                    }}
                  >
                    <ClearRoundedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              <Tooltip title="Salvar (Enter)">
                <IconButton
                  color="primary"
                  size="small"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => confirmEdit()}
                  disabled={!isValid}
                >
                  <DoneRoundedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </InputAdornment>
          ),
        }}
        sx={{ "& .MuiInputBase-root": { height: 44 } }}
      />

      {recentToShow.length > 0 && (
        <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
          <HistoryRoundedIcon fontSize="small" sx={{ color: "text.secondary" }} />
          {recentToShow.map((slug) => (
            <Chip
              key={slug}
              label={slug}
              size="small"
              variant="outlined"
              onClick={() => {
                const v = toLower(slug);
                setDraft(v);
                confirmEdit(v);
              }}
              onDelete={() => {
                const next = removeRecent(slug);
                setRecent(next);
              }}
            />
          ))}
        </Stack>
      )}
    </Stack>
  );
}
